import { Concept, ConceptId, InterviewBlock } from "../types";
import {
  leastFill,
  balanceSegments,
  type LeastFillData,
  type BalanceSegmentsData,
  type AllConceptSamplingData,
  randomShuffle,
} from "./functions";
import { assertNever } from "@/packages/ts-utils";
import { logger } from "@/utils/logging/isomorphic";

export const DEFAULT_CONCEPT_SAMPLING_FUNCTION_ID = "leastFill";

interface ConceptSamplingFunctionBase {
  displayName: string;
  description: string;
}

interface LeastFillFunction extends ConceptSamplingFunctionBase {
  id: "leastFill";
  function: typeof leastFill;
}

interface BalanceSegmentsFunction extends ConceptSamplingFunctionBase {
  id: "balanceSegments";
  function: typeof balanceSegments;
}

export type ConceptSamplingFunction =
  | LeastFillFunction
  | BalanceSegmentsFunction;

type SortConceptsBaseArgs = {
  concepts: Concept[];
  block: InterviewBlock;
  // seeded rng; when set, sampling is deterministic for a given response + block
  rng?: () => number;
};

type RandomShuffleParams = {
  functionId: "randomShuffle";
  functionArgs: SortConceptsBaseArgs;
};

type LeastFillParams = {
  functionId: "leastFill";
  functionArgs: SortConceptsBaseArgs & { data: LeastFillData };
};

type BalanceSegmentsParams = {
  functionId: "balanceSegments";
  functionArgs: SortConceptsBaseArgs & { data: BalanceSegmentsData };
};

type DynamicParams = {
  functionId: ConceptSamplingFunction["id"];
  functionArgs: SortConceptsBaseArgs & { data?: AllConceptSamplingData };
};

export const USER_SELECTABLE_CONCEPT_SAMPLING_FUNCTIONS: ConceptSamplingFunction[] =
  [
    {
      id: "leastFill",
      displayName: "Least Fill",
      description: "Sample the concepts with the fewest responses",
      function: leastFill,
    },
    {
      id: "balanceSegments",
      displayName: "Balance Segments",
      description:
        "Sample the concepts where the participant's segments are most underrepresented",
      function: balanceSegments,
    },
  ];

// TODO: eliminate dynamic params and force the caller to know that it's passing valid data
export const safeValidateDynamicSamplingParams = (
  params: DynamicParams
): LeastFillParams | BalanceSegmentsParams | RandomShuffleParams => {
  const randomShuffleParams: RandomShuffleParams = {
    functionId: "randomShuffle",
    functionArgs: params.functionArgs,
  };

  if (!params.functionArgs.data) {
    return randomShuffleParams;
  }

  switch (params.functionId) {
    case "leastFill": {
      const { conceptSamplingCount } = params.functionArgs.data;

      if (!conceptSamplingCount) {
        logger.captureException(
          new Error("Concept sampling count is required for least fill")
        );
        return randomShuffleParams;
      }

      return {
        functionId: "leastFill",
        functionArgs: {
          ...params.functionArgs,
          data: {
            conceptSamplingCount,
          },
        },
      };
    }
    case "balanceSegments": {
      const {
        conceptSamplingCount,
        responseSegmentIds,
        conceptIdToSegmentCounts,
        segmentCounts,
      } = params.functionArgs.data;
      if (
        !conceptSamplingCount ||
        !responseSegmentIds ||
        !conceptIdToSegmentCounts ||
        !segmentCounts
      ) {
        logger.captureException(
          new Error(
            "Concept sampling count, response segment ids, and concept id to segment counts are required for balance segments"
          )
        );
        return randomShuffleParams;
      }
      return {
        functionId: "balanceSegments",
        functionArgs: {
          ...params.functionArgs,
          data: {
            conceptSamplingCount,
            responseSegmentIds,
            conceptIdToSegmentCounts,
            segmentCounts,
          },
        },
      };
    }
    default:
      assertNever(params.functionId);
  }
};

export function sortConceptsForSampling(
  params: LeastFillParams | BalanceSegmentsParams | RandomShuffleParams
): ConceptId[] {
  const {
    functionArgs: { concepts, block, rng },
  } = params;

  switch (params.functionId) {
    case "randomShuffle": {
      return randomShuffle({
        concepts,
        block,
        data: null,
        rng,
      });
    }
    case "leastFill": {
      const { data } = params.functionArgs;

      return leastFill({
        concepts,
        block,
        data,
        rng,
      });
    }
    case "balanceSegments": {
      const { data } = params.functionArgs;
      return balanceSegments({
        concepts,
        block,
        data,
        rng,
      });
    }
    default:
      assertNever(params);
  }
}
